/**
 * Event Bridge - Declarative routing from nexusEvents to Socket.IO.
 *
 * Modules register BridgeRules to automatically forward internal events
 * to Socket.IO rooms. This replaces manual `io.to().emit()` calls
 * with a centralized, auditable routing layer.
 */

import type { EventEmitter2 } from 'eventemitter2'
import type { BridgeRule } from './types.js'
import { nexusEvents } from './emitter.js'
import {
  isSocketIOInitialized,
  emitToRoom,
  emitToUser,
  emitToAll,
  emitToAuthenticated
} from './socket.js'
import { RealtimeDebouncer } from './realtime-debouncer.js'
import { logger } from '@kernel/logger/index.js'

interface RegisteredListener {
  event: string
  handler: (...args: unknown[]) => void
}

class EventBridge {
  private rules: BridgeRule[] = []
  private listeners: RegisteredListener[] = []
  private debouncer = new RealtimeDebouncer()
  private initialized = false

  /**
   * Register a bridge rule.
   * If the bridge is already initialized, the listener is attached immediately.
   */
  registerRule(rule: BridgeRule): void {
    // Avoid duplicate rules for the same nexusEvent+socketEvent
    const exists = this.rules.some(
      r => r.nexusEvent === rule.nexusEvent && r.socketEvent === rule.socketEvent
    )
    if (exists) {
      logger.debug({ nexusEvent: rule.nexusEvent, socketEvent: rule.socketEvent }, 'Bridge rule already registered, skipping')
      return
    }

    this.rules.push(rule)

    if (this.initialized) {
      this.attachRule(rule)
    }
  }

  /**
   * Remove all rules for a specific nexusEvent.
   */
  removeRule(nexusEvent: string): void {
    // Remove listeners
    const toRemove = this.listeners.filter(l => l.event === nexusEvent)
    for (const { event, handler } of toRemove) {
      ;(nexusEvents as unknown as EventEmitter2).off(event, handler)
    }
    this.listeners = this.listeners.filter(l => l.event !== nexusEvent)

    // Remove rules
    this.rules = this.rules.filter(r => r.nexusEvent !== nexusEvent)
  }

  /**
   * Initialize the bridge — attach all registered rules to nexusEvents.
   * Should be called after Socket.IO is ready.
   */
  init(): void {
    if (this.initialized) return

    for (const rule of this.rules) {
      this.attachRule(rule)
    }

    this.initialized = true
    logger.debug({ ruleCount: this.rules.length }, 'Event bridge initialized')
  }

  /**
   * Destroy — remove all listeners and clear state.
   */
  destroy(): void {
    for (const { event, handler } of this.listeners) {
      ;(nexusEvents as unknown as EventEmitter2).off(event, handler)
    }
    this.listeners = []
    this.rules = []
    this.debouncer.clear()
    this.initialized = false
  }

  /**
   * Get all registered rules (for debugging/introspection).
   */
  getRules(): readonly BridgeRule[] {
    return this.rules
  }

  private attachRule(rule: BridgeRule): void {
    const handler = (payload: unknown) => {
      logger.debug({ nexusEvent: rule.nexusEvent, socketEvent: rule.socketEvent }, '[DEBUG-RT] Bridge rule fired')
      if (!isSocketIOInitialized()) {
        logger.debug('[DEBUG-RT] Socket.IO not initialized, skipping bridge')
        return
      }

      const emit = () => {
        const transformed = rule.transform ? rule.transform(payload) : payload
        this.routeToSocket(rule, transformed)
      }

      if (rule.debounceMs && rule.debounceMs > 0) {
        // Include room + record ID in key so bulk updates to N distinct records
        // each debounce independently (no event loss).
        const room = rule.target.type === 'room'
          ? (typeof rule.target.room === 'function' ? 'dynamic' : rule.target.room)
          : rule.target.type
        const recordId = typeof payload === 'object' && payload && 'id' in payload
          ? String((payload as { id: unknown }).id ?? '')
          : ''
        const key = `${rule.nexusEvent}:${rule.socketEvent}:${room}:${recordId}`
        this.debouncer.debounce(key, rule.debounceMs, payload, () => {
          const transformed = rule.transform ? rule.transform(payload) : payload
          this.routeToSocket(rule, transformed)
        }, (discardedKey) => {
          logger.debug({ key: discardedKey, nexusEvent: rule.nexusEvent }, 'Event debounced: replaced pending event')
        })
      } else {
        emit()
      }
    }

    ;(nexusEvents as unknown as EventEmitter2).on(rule.nexusEvent, handler)
    this.listeners.push({ event: rule.nexusEvent, handler })
  }

  private routeToSocket(rule: BridgeRule, payload: unknown): void {
    const { target, socketEvent } = rule
    logger.debug({ socketEvent, targetType: target.type }, '[DEBUG-RT] routeToSocket called')

    switch (target.type) {
      case 'all':
        emitToAll(socketEvent, payload)
        break
      case 'authenticated':
        emitToAuthenticated(socketEvent, payload)
        break
      case 'user': {
        const userId = typeof target.userId === 'function'
          ? target.userId(payload)
          : target.userId
        emitToUser(userId, socketEvent, payload)
        break
      }
      case 'room': {
        const room = typeof target.room === 'function'
          ? target.room(payload)
          : target.room
        logger.debug({ room, socketEvent }, '[DEBUG-RT] emitting to room')
        emitToRoom(room, socketEvent, payload)
        break
      }
    }
  }
}

export const eventBridge = new EventBridge()
